import * as React from 'react'
import styled from 'styled-components'
import { clock } from '../../../../common/src/clock'
import Button from '../../../../common/src/components/Button'
import { Spacer, BodyTextSpan } from '../../../../common/src/components/core'
import { ColorName } from '../../../../common/src/constants'
import { isoSecondsFmt, diffSecs, diffFmtSecsMinutes } from '../../../../common/src/date'
import Time from '../../../../common/src/time'
import {
  FetchMostRecentClockEventQuery,
  UserClockFragment,
  ChangeClockMutationMutation,
  FetchUserContextQuery,
} from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { fetchMostRecentClockEventQuery, changeClockMutation } from '../../graphql/queries/ClockInClockOutQueries'

const Container = styled.div`
  display: inline-block;
`

interface ClockInClockOutImplState {
  clockedIn: boolean
  errored: boolean
  loading: boolean
  timeNow: string
  mostRecentClockEvent: FetchMostRecentClockEventQuery['self']['mostRecentClockEvent'] | null
}

interface ClockInClockOutImplProps {
  user: FetchUserContextQuery['self']
}

export default class ClockInClockOut extends React.Component<ClockInClockOutImplProps, ClockInClockOutImplState> {
  constructor(props: ClockInClockOutImplProps) {
    super(props)

    this.state = {
      clockedIn: false,
      loading: true,
      errored: false,
      timeNow: isoSecondsFmt(Time.now()),
      mostRecentClockEvent: null,
    }
  }

  refreshTime = (timeNow: string) => {
    this.setState({ timeNow })
  }

  componentWillUnmount() {
    clock.removeListener(this.refreshTime)
  }

  componentDidMount() {
    clock.addListener(this.refreshTime)

    const query = fetchMostRecentClockEventQuery
    apolloWebClient
      .query<FetchMostRecentClockEventQuery>({ query })
      .then(r => this.receiveNewLatestClockEvent(r.data?.self?.mostRecentClockEvent))
      .catch(this.handleClockError)
  }

  render() {
    return (
      <Container>
        <Button
          width={150}
          disabled={this.state.loading || this.state.errored}
          color={this.getButtonColorName()}
          onClick={this.onClick}
        >
          {this.getClockActionText()}
        </Button>
        <Spacer width={16} />
        <BodyTextSpan>{this.getClockInfoString()}</BodyTextSpan>
        <Spacer width={16} />
      </Container>
    )
  }

  private receiveNewLatestClockEvent(e: UserClockFragment['mostRecentClockEvent'] | undefined | null) {
    this.setState({
      loading: false,
      clockedIn: !!e && !!e.clockInTime && !e.clockOutTime,
      mostRecentClockEvent: e || null,
    })
  }

  private handleClockError = (e: Error) => {
    this.setState({ errored: true }, () => console.log(e))
  }

  private getClockInfoString() {
    const { timeNow, clockedIn, mostRecentClockEvent } = this.state

    if (this.state.loading) {
      return ''
    } else if (this.state.errored) {
      return 'error clocking your hours'
    } else if (clockedIn && mostRecentClockEvent != null) {
      const ds = Math.max(0, diffSecs(timeNow, mostRecentClockEvent.clockInTime))
      return `clocked in ${diffFmtSecsMinutes(ds)} ago`
    } else if (!clockedIn && mostRecentClockEvent && mostRecentClockEvent.clockOutTime) {
      const ds = Math.max(0, diffSecs(timeNow, mostRecentClockEvent.clockOutTime))
      return `clocked out ${diffFmtSecsMinutes(ds)} ago`
    } else {
      return ''
    }
  }

  private getClockActionText() {
    if (this.state.loading) {
      return 'Loading...'
    } else if (this.state.clockedIn) {
      return 'Clock Out'
    } else {
      return 'Clock In'
    }
  }

  private getButtonColorName(): ColorName {
    if (this.state.errored) {
      return 'coral'
    } else if (this.state.loading) {
      return 'midway'
    } else if (this.state.clockedIn) {
      return 'coral'
    } else {
      return 'mint'
    }
  }

  private onClick = () => {
    if (this.state.loading) {
      return
    }
    this.setState({ loading: true }, () => {
      if (this.state.clockedIn) {
        this.clockInOut(false)
      } else {
        this.clockInOut(true) // clock in
      }
    })
  }

  private clockInOut(nextClockedInState: boolean) {
    const mutation = changeClockMutation
    const variables = { isClockedIn: nextClockedInState }
    apolloWebClient
      .mutate<ChangeClockMutationMutation>({ mutation, variables })
      .then(p => this.receiveNewLatestClockEvent(p.data?.setClockedIn?.user?.mostRecentClockEvent))
      .catch((e: any) => this.handleClockError(e as Error))
  }
}
